// Summary — KPIs derived from a forecast row array. Pure function, no state.

/**
 * @param {Array<object>} rows  output of forecast.runForecast
 */
export function summarize(rows) {
  if (!rows || rows.length === 0) {
    return {
      endY3Users: 0,
      endY3AppAccounts: 0,
      endY3Cardholders: 0,
      endY3Pro: 0,
      monthlyRunRateRev: 0,
      subscriptionArr: 0,
      totalRev: 0,
      totalCost: 0,
      totalCm: 0,
      totalContributionProfit: 0,
      totalAcquisitionCost: 0,
      totalFixedOpex: 0,
      totalOperatingProfit: 0,
      grossMarginPct: 0,
      breakevenMonth: null,
      operatingBreakEvenMonth: null,
      peakFundingNeed: 0,
      cumCashEnd: 0,
      cumGmvEnd: 0,
      perUserAnnualNet: 0,
      netStablecoinEconomics: 0,
      stablecoinYieldRev: 0,
      walletOperationCost: 0,
      moneyBackCost: 0,
      moneyBackUserRewardValue: 0,
      moneyBackPartnerFundedValue: 0,
      moneyBackGeneratedAppAccounts: 0,
      breakEvenRequirements: {},
      lastMonth: null,
    };
  }
  const last = rows[rows.length - 1];

  const totalRev = rows.reduce((a, r) => a + r.totalRev, 0);
  const totalCost = rows.reduce((a, r) => a + r.totalCost, 0);
  const totalContributionProfit = rows.reduce((a, r) => a + (r.contributionProfit ?? 0), 0);
  const totalAcquisitionCost = rows.reduce((a, r) => a + (r.acquisitionCost ?? 0), 0);
  const totalFixedOpex = rows.reduce((a, r) => a + (r.fixedOpexCost ?? 0), 0);
  const totalCm = totalRev - totalCost;
  const totalOperatingProfit = totalCm;

  // Breakeven = first month where cumCash crosses to positive AND stays positive
  let breakeven = null;
  if (last.cumCash > 0) {
    for (let i = rows.length - 1; i >= 0; i--) {
      if (rows[i].cumCash <= 0) {
        breakeven = i + 2;
        break;
      }
      if (i === 0) breakeven = 1;
    }
  }

  const peakBurn = rows.reduce((a, r) => Math.min(a, r.netPL), 0);
  const trough = rows.reduce((a, r) => Math.min(a, r.cumCash), 0);

  // Per-user annual operating profit — average across the 36-month horizon
  const avgUsers = rows.reduce((a, r) => a + r.totalUsers, 0) / rows.length;
  const annualizedCm = (totalCm / rows.length) * 12;
  const perUserAnnualNet = avgUsers > 0 ? annualizedCm / avgUsers : 0;
  const lastContributionPerCardholder =
    last.activeCardholders > 0 ? (last.contributionProfit ?? 0) / last.activeCardholders : 0;
  const lastCardRevenuePerGmv =
    last.gmvTotal > 0 ? (last.totalRev - last.earnRev) / last.gmvTotal : 0;
  const lastCardCostPerGmv =
    last.gmvTotal > 0
      ? ((last.variableProductCost ?? 0) - (last.walletOperationCost ?? 0)) / last.gmvTotal
      : 0;
  const cardNetRate = lastCardRevenuePerGmv - lastCardCostPerGmv;
  const fixedOpex = last.fixedOpexCost ?? 0;
  const stablecoinMonthlyYieldRate =
    (last.stablecoinInvestBalance ?? 0) > 0
      ? (last.stablecoinYieldRev ?? 0) / last.stablecoinInvestBalance
      : 0;

  return {
    endY3Users: last.totalUsers,
    endY3AppAccounts: last.appAccounts ?? last.totalUsers,
    endY3ActiveAppAccounts: last.activeAppAccounts ?? last.activeUsers,
    endY3Cardholders: last.totalCardholders ?? last.totalUsers,
    endY3Pro: last.proUsers,
    endY3Free: last.freeUsers,
    monthlyRunRateRev: last.totalRev * 12,
    subscriptionArr: (last.proSubRev ?? 0) * 12,
    totalRev,
    totalCost,
    totalCm,
    totalContributionProfit,
    totalAcquisitionCost,
    totalFixedOpex,
    totalOperatingProfit,
    grossMarginPct: totalRev > 0 ? totalCm / totalRev : 0,
    breakevenMonth: breakeven,
    operatingBreakEvenMonth: breakeven,
    peakBurn,
    trough,
    peakFundingNeed: Math.abs(Math.min(0, trough)),
    cumCashEnd: last.cumCash,
    cumGmvEnd: last.cumGmv,
    perUserAnnualNet,
    netStablecoinEconomics: rows.reduce((a, r) => a + (r.netStablecoinEconomics ?? 0), 0),
    stablecoinYieldRev: rows.reduce((a, r) => a + (r.stablecoinYieldRev ?? r.earnRev ?? 0), 0),
    walletOperationCost: rows.reduce((a, r) => a + (r.walletOperationCost ?? 0), 0),
    moneyBackCost: rows.reduce((a, r) => a + (r.moneyBackCost ?? 0), 0),
    moneyBackUserRewardValue: rows.reduce((a, r) => a + (r.moneyBackUserRewardValue ?? 0), 0),
    moneyBackPartnerFundedValue: rows.reduce((a, r) => a + (r.moneyBackPartnerFundedValue ?? 0), 0),
    moneyBackGeneratedAppAccounts: rows.reduce(
      (a, r) => a + (r.moneyBackGeneratedAppAccounts ?? 0),
      0,
    ),
    wirexFloorAsPctRevenue: last.totalRev > 0 ? (last.wirexFloorCost ?? 0) / last.totalRev : 0,
    proSubAsPctRevenue: last.totalRev > 0 ? (last.proSubRev ?? 0) / last.totalRev : 0,
    breakEvenRequirements: {
      monthlyRevenue: last.totalRev ?? 0,
      monthlyVariableCost: last.variableProductCost ?? 0,
      monthlyContributionProfit: last.contributionProfit ?? 0,
      monthlyAcquisitionCost: last.acquisitionCost ?? 0,
      monthlyFixedOpex: fixedOpex,
      monthlyOperatingGap: Math.max(0, -(last.operatingProfit ?? 0)),
      variableCostReductionForSteadyState:
        last.totalRev > fixedOpex
          ? Math.max(0, (last.variableProductCost ?? 0) - (last.totalRev - fixedOpex))
          : null,
      contributionImprovementNeeded: Math.max(
        0,
        fixedOpex + (last.acquisitionCost ?? 0) - (last.contributionProfit ?? 0),
      ),
      activeCardholders:
        lastContributionPerCardholder > 0 ? fixedOpex / lastContributionPerCardholder : null,
      monthlyCardGmv: cardNetRate > 0 ? fixedOpex / cardNetRate : null,
      maxFixedOpex: last.contributionProfit ?? 0,
      maxAppAccountCac:
        (last.newAppAccounts ?? 0) > 0
          ? Math.max(
              0,
              ((last.contributionProfit ?? 0) - (last.cardActivationCost ?? 0) - fixedOpex) /
                last.newAppAccounts,
            )
          : null,
      investBalanceToCoverWalletCost:
        stablecoinMonthlyYieldRate > 0
          ? (last.walletOperationCost ?? 0) / stablecoinMonthlyYieldRate
          : null,
    },
    lastMonth: last,
  };
}

/**
 * Stack revenue / cost lines for the breakdown chart.
 */
export function aggregateLines(rows) {
  const sum = (k) => rows.reduce((a, r) => a + (r[k] ?? 0), 0);
  return {
    revenue: {
      proSub: sum('proSubRev'),
      interchange: sum('interchangeRev'),
      fx: sum('fxRev'),
      topup: sum('topupRev'),
      swap: sum('swapRev'),
      earn: sum('earnRev'),
      aiOverage: sum('aiOverageRev'),
      aiOutcome: sum('aiOutcomeRev'),
      aiReferral: sum('aiReferralRev'),
      b2bPartnerCampaigns: sum('b2bPartnerCampaignsRev'),
      b2bClosedLoop: sum('b2bClosedLoopRev'),
      b2bWhiteLabel: sum('b2bWhiteLabelRev'),
    },
    cost: {
      cashbackPoints: sum('cashbackPointsCost'),
      fxAbsorbed: sum('fxAbsorbedCost'),
      aiCompute: sum('aiComputeCost'),
      topupFunding: sum('topupFundingCost'),
      wirexFloor: sum('wirexFloorCost'),
      disputes: sum('disputeCost'),
      cardProduction: sum('cardProductionCost'),
      referral: sum('referralCost'),
      moneyBack: sum('moneyBackCost'),
      walletOperations: sum('walletOperationCost'),
      appAccountCac: sum('appAccountCacCost'),
      cardActivation: sum('cardActivationCost'),
      fixedOpex: sum('fixedOpexCost'),
    },
  };
}

/**
 * Per-tier P&L roll-up for the Excel "Per_Tier_PnL" sheet and Detail page.
 * Sums over the 36-month horizon for each tier.
 */
export function perTierPnl(rows) {
  if (!rows.length) return [];
  const keys = Object.keys(rows[0].perTier || {});
  return keys.map((k) => {
    let totalActive = 0,
      totalGmv = 0,
      totalRev = 0,
      totalCost = 0;
    for (const r of rows) {
      const t = r.perTier[k];
      totalActive += t.active;
      totalGmv += t.gmv;
      totalRev += t.rev;
      totalCost += t.cost;
    }
    return {
      tier: k,
      avgActive: totalActive / rows.length,
      totalGmv,
      totalRev,
      totalCost,
      totalNet: totalRev - totalCost,
      avgNetPerActive: totalActive > 0 ? (totalRev - totalCost) / totalActive : 0,
    };
  });
}
